"use client";

import { cn } from "../../lib";
import { QuizTruncationScroll } from "../quiz/quiz-truncation-scroll";
import {
	OCTAGON_BUTTON_DEFAULT_SIZE_PX,
	resolveOctagonBadgeBottomOffsetPx,
} from "./octagon-geometry";

/** Default offset for bottom badge pills on satellite thumbs. */
export const OCTAGON_BUTTON_BADGE_OFFSET_UP_PX = 5;

/**
 * Approx rendered width of the SUPPORT action-tab pill (longest rail badge).
 * Hotkey move-name pills cap here so long names scroll instead of stretching.
 */
export const OCTAGON_BUTTON_BADGE_SUPPORT_MAX_W_CLASS = "max-w-[4.25rem]";

export interface OctagonButtonBottomLabelProps {
	text: string;
	/** Align pill bottom to inner hub (not outer meter ring); outer ring diameter in px. */
	alignBottomToHubOfRingPx?: number;
	/** Extra px downward from the hub-aligned bottom (positive = lower on screen). */
	offsetDownPx?: number;
	/**
	 * Extra horizontal nudge after the default +2px optical center
	 * (positive = right). Circle hubs pass `-2` to cancel that default.
	 */
	offsetLeftPx?: number;
	/**
	 * Cap pill width and auto-scroll overflowing text (battle hotkey move names).
	 * Defaults to {@link OCTAGON_BUTTON_BADGE_SUPPORT_MAX_W_CLASS} when true.
	 */
	truncationScroll?: boolean;
	/** Override max-width when {@link truncationScroll} is on. */
	maxWidthClassName?: string;
	className?: string;
}

export function OctagonButtonBottomLabel({
	text,
	alignBottomToHubOfRingPx,
	offsetDownPx = 0,
	offsetLeftPx = 0,
	truncationScroll = false,
	maxWidthClassName = OCTAGON_BUTTON_BADGE_SUPPORT_MAX_W_CLASS,
	className,
}: OctagonButtonBottomLabelProps) {
	const hubAlignedBottomPx =
		alignBottomToHubOfRingPx !== undefined
			? resolveOctagonBadgeBottomOffsetPx(alignBottomToHubOfRingPx)
			: undefined;
	/** +2px matches `.sq-octagon-button-icon` optical center nudge. */
	const translateXPx = 2 + offsetLeftPx;
	const hangBelowHalf =
		hubAlignedBottomPx === undefined
			? `translateX(calc(-50% + ${translateXPx}px)) translateY(50%)`
			: `translateX(calc(-50% + ${translateXPx}px))`;
	const bottomPx =
		hubAlignedBottomPx !== undefined
			? Math.max(0, hubAlignedBottomPx - offsetDownPx)
			: OCTAGON_BUTTON_BADGE_OFFSET_UP_PX - offsetDownPx;

	return (
		<span
			className={cn(
				"sq-octagon-button-badge pointer-events-none absolute left-1/2 z-10 inline-flex h-auto w-fit items-center justify-center whitespace-nowrap rounded-sm border-2 border-[#111111] bg-white px-1.5 py-0.5 text-center font-black text-[9px] uppercase leading-none text-slate-950 shadow-sm sm:text-[10px]",
				truncationScroll ? maxWidthClassName : "max-w-none",
				className,
			)}
			style={{
				bottom: bottomPx,
				// Single transform — Tailwind `translate-x` + `translate-y` utilities
				// conflict and drop horizontal centering when both are present.
				transform: hangBelowHalf,
			}}
			aria-hidden
		>
			{/*
			 * `tracking-wide` adds trailing space after the last glyph; match it with
			 * equal start padding so the ink stays optically centered in the pill.
			 */}
			{truncationScroll ? (
				<QuizTruncationScroll
					mode="horizontal"
					className="min-w-0 w-full text-center font-black uppercase leading-none tracking-wide"
					title={text}
				>
					<span className="pl-[0.025em] tracking-wide">{text}</span>
				</QuizTruncationScroll>
			) : (
				<span className="pl-[0.025em] tracking-wide">{text}</span>
			)}
		</span>
	);
}

export function resolveOctagonButtonHostSizeStyle(
	size = OCTAGON_BUTTON_DEFAULT_SIZE_PX,
): { width: number; height: number } {
	return { width: size, height: size };
}
